import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { globalStores } from '@/editor/stores';
import { OperateAbstract } from './OperateAbstract';
import FurnitureEntity from '@/editor/entities/furnitureEntity';
import { EEntityType } from '@/editor/types';
import { IFurniture } from '@/editor/data/model/furniture';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { cloneDeep } from 'lodash-es';
import { calculateBoxRulerInfos } from '@/editor/stores/utils/ruler/model';
import { getBoxRayDetectOutlines } from '@/editor/stores/utils/ruler/outlines';
import { FurnitureAbsorbManager } from '@/editor/manager/absorb/box/furnitureAbsorbManager';
import { OperateSelectedBoxes } from '@/editor/controller/helper/operateSelectedBoxes';
import { VIRTUAL_ID_SUFFIX } from '@/editor/controller/helper/constants';

export class OperateFurniture extends OperateAbstract<EEntityType.Furniture> {
  private _checkedEntity: FurnitureEntity | null | undefined = null;

  private _virtualFurnitureData: IFurniture | null | undefined = null;
  private _virtualFurnitureEntity: FurnitureEntity | null = null;
  private _virtualRulerModel = new RulerModel();

  private _extraOulines: { start: IPoint2d; end: IPoint2d }[] = [];

  private _furnitureAbsorbManager = new FurnitureAbsorbManager();
  private _selectedBoxes = new OperateSelectedBoxes();

  constructor() {
    super();
  }

  get selected() {
    return globalStores.editorStateStore.selected;
  }

  set checkedEntity(entity: FurnitureEntity | null | undefined) {
    if (!entity) return;

    this._checkedEntity = entity;
    // this._checkedEntity.selected = false;

    this._furnitureAbsorbManager.updateData({ excludeFurnitureIds: [this._checkedEntity.id] });
  }

  get checkedEntity() {
    return this._checkedEntity;
  }

  override startMove(_point: IPoint2d) {
    this.moving = true;
    if (!this._checkedEntity) return;

    const furnitureData = globalStores.modelStore.getFurnitureData(this._checkedEntity.id);
    if (!furnitureData) return;

    this._checkedEntity.setVisible(false);

    this._virtualFurnitureData = cloneDeep(furnitureData);
    this._virtualFurnitureEntity = new FurnitureEntity({ id: this._virtualFurnitureData.id + VIRTUAL_ID_SUFFIX });

    this._extraOulines = getBoxRayDetectOutlines([this._checkedEntity.id]);

    this.updateVirtualEntity();
    this.updateBoxes();
  }

  move(point: IPoint2d) {
    this.moving = true;

    if (!this._checkedEntity || !this._virtualFurnitureData) {
      return point;
    }

    const furnitureData = globalStores.modelStore.getFurnitureData(this._checkedEntity.id);
    if (!furnitureData) return point;

    const boxData = {
      center: point,
      width: furnitureData.width,
      thickness: furnitureData.thickness,
      rotation: furnitureData.rotation
    };
    const absorbResult = this._furnitureAbsorbManager.calculateThenRender(boxData);
    if (absorbResult) {
      boxData.center = absorbResult.absorbPoint;
      boxData.rotation = absorbResult.rotation;
    }

    this._virtualFurnitureData.center = boxData.center;
    this._virtualFurnitureData.rotation = boxData.rotation;
    this.updateVirtualEntity();
    this.updateBoxes({ x: boxData.center.x, y: boxData.center.y, rotation: boxData.rotation });

    return [boxData.center, boxData.rotation] satisfies [IPoint2d, number];
  }

  override moveByOffset(offset: IPoint2d) {
    if (!this._checkedEntity || !this._virtualFurnitureData) return;

    this.moving = true;

    const center = Point2dUtils.added(this._virtualFurnitureData.center, offset);
    this._virtualFurnitureData.center = center;

    this.updateVirtualEntity();
    this.updateBoxes({ x: center.x, y: center.y, rotation: this._virtualFurnitureData.rotation });
  }

  completeMove(_point: IPoint2d) {
    this.moving = false;
    if (!this._checkedEntity || !this._virtualFurnitureData) return;

    globalStores.furnitureOperation.modifyFurniture(this._checkedEntity.id, {
      center: this._virtualFurnitureData.center,
      rotation: this._virtualFurnitureData.rotation
    });
    this._checkedEntity.setVisible(true);

    this._virtualFurnitureData = null;
    this._virtualRulerModel.clear();
    this._furnitureAbsorbManager.clearModel();
  }

  private updateVirtualEntity() {
    if (!this._virtualFurnitureData || !this._virtualFurnitureEntity) return;

    this._virtualFurnitureEntity.update(this._virtualFurnitureData);

    const rulerInfos = calculateBoxRulerInfos(this._virtualFurnitureData, this._extraOulines);

    this._virtualRulerModel.updateModel(rulerInfos);
  }

  private updateBoxes(params?: { x: number; y: number; rotation: number }) {
    if (!this._checkedEntity) return;

    this._selectedBoxes.update({ type: EEntityType.Furniture, entityId: this._checkedEntity.id }, params);
  }

  clear() {
    this.checkedEntity = null;
    this.moving = false;
    this.pickPoint = null;
    this._virtualFurnitureData = null;
    this._virtualFurnitureEntity?.dispose();
    this._virtualFurnitureEntity = null;
    this._virtualRulerModel.clear();
    this._furnitureAbsorbManager.clearModel();
    this._selectedBoxes.clear();
  }

  dispose() {
    this.clear();

    this._virtualRulerModel.dispose();
    this._selectedBoxes.dispose();
    this._furnitureAbsorbManager.dispose();
  }
}
